import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Lock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { LOGO_URL } from "@/lib/content";
import { portalAcceptInvite } from "@/lib/portalApi";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PortalAcceptInvite() {
  const { token } = useParams();
  const nav = useNavigate();
  const [info, setInfo] = useState(null); // {email, venue_name} or null
  const [status, setStatus] = useState("checking"); // checking | ok | invalid | expired
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/portal/invite/${token}`);
        setInfo(data);
        setStatus("ok");
      } catch (err) {
        setStatus(err?.response?.status === 410 ? "expired" : "invalid");
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords don't match.");
      return;
    }
    setSubmitting(true);
    try {
      await portalAcceptInvite(token, password);
      toast.success("Password set. Welcome to your portal.");
      nav("/portal", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Failed to set password.";
      toast.error(typeof detail === "string" ? detail : "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "bg-black border-white/15 rounded-none h-12 text-[#F5F5F0] focus-visible:ring-[#D9A6F5] focus-visible:ring-offset-0";

  return (
    <div
      data-testid="portal-accept-page"
      className="min-h-screen bg-black text-[#F5F5F0] grain flex items-center justify-center px-6"
    >
      <div className="relative z-[2] w-full max-w-md">
        <div className="flex justify-center mb-10">
          <img src={LOGO_URL} alt="Swingle" className="h-24 md:h-28 w-auto object-contain" />
        </div>
        <div className="brackets bg-[#0A0A0A] border border-white/10 p-10">
          <div className="flex items-center justify-center gap-3 text-xs uppercase tracking-[0.3em] text-[#D9A6F5] mb-4">
            <Lock size={14} /> Venue Portal
          </div>

          {status === "checking" && (
            <div className="text-center py-6 text-[#A3A3A3]">
              <Loader2 size={20} className="animate-spin inline text-[#D9A6F5]" />
            </div>
          )}

          {status === "invalid" && (
            <div className="text-center py-4">
              <h1 className="text-2xl font-black tracking-tighter mb-3">Invite not found</h1>
              <p className="text-sm text-[#A3A3A3]">
                This link is invalid or has already been used. Ask your admin to send you a fresh
                invite.
              </p>
            </div>
          )}

          {status === "expired" && (
            <div className="text-center py-4">
              <h1 className="text-2xl font-black tracking-tighter mb-3">Invite expired</h1>
              <p className="text-sm text-[#A3A3A3]">
                Your invite link has expired. Ask your admin to send you a new one.
              </p>
            </div>
          )}

          {status === "ok" && info && (
            <>
              <h1 className="text-center text-3xl md:text-4xl font-black tracking-tighter mb-2">
                Set your password
              </h1>
              <p className="text-center text-sm text-[#A3A3A3] mb-8">
                For <span className="text-[#D9A6F5]">{info.venue_name}</span> · {info.email}
              </p>

              <form onSubmit={submit} className="space-y-5" data-testid="portal-accept-form" noValidate>
                <div className="space-y-2">
                  <label className="text-xs uppercase tracking-[0.22em] text-[#A3A3A3] font-medium">
                    Password (min 8 characters)
                  </label>
                  <Input
                    type="password"
                    data-testid="portal-accept-password"
                    autoFocus
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={inputCls}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs uppercase tracking-[0.22em] text-[#A3A3A3] font-medium">
                    Confirm password
                  </label>
                  <Input
                    type="password"
                    data-testid="portal-accept-confirm"
                    autoComplete="new-password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className={inputCls}
                    required
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting || !password || password !== confirm}
                  data-testid="portal-accept-submit"
                  className="w-full inline-flex items-center justify-center gap-3 bg-[#D9A6F5] text-black px-8 py-4 text-sm uppercase tracking-[0.22em] font-bold hover:bg-[#E8C8FF] disabled:opacity-40 transition-colors"
                >
                  {submitting ? <Loader2 size={16} className="animate-spin" /> : null}
                  Enter Portal
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
